import { Directive, ElementRef, HostListener, Input, NgModule, OnInit, Optional } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, timeoutWith } from 'rxjs/operators';

import { WindowService } from './window.service';

export type RouterLinkCommand = any[] | string;

export type TrackLinkFunction = () => Observable<unknown>;

@Directive({
  selector: '[omTrackLink]',
})
export class TrackLinkDirective implements OnInit {
  @Input() target: string;
  @Input() omTrackLink?: TrackLinkFunction | undefined;
  @Input() routerLink: RouterLinkCommand;
  @Input() state: Record<string, any>;

  private anchorElement: HTMLAnchorElement;

  constructor(
    private router: Router,
    @Optional() private link: RouterLink,
    private windowService: WindowService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit() {
    if (!this.omTrackLink) {
      return;
    }

    if (this.elementRef.nativeElement instanceof HTMLAnchorElement) {
      this.anchorElement = this.elementRef.nativeElement;
    } else {
      this.anchorElement = this.elementRef.nativeElement.getElementsByTagName('a')[0];
    }

    this.preventDefaultNavigation();
  }

  @HostListener('click', ['$event']) onClick(event: MouseEvent) {
    if (!this.omTrackLink) {
      return;
    }

    const newTab = event.metaKey || event.ctrlKey || this.targetIsBlankTab();
    if (!this.routerLink) {
      event.preventDefault();
    }

    this.omTrackLink()
      .pipe(
        /*
         timeoutWith gives omTrackLink a reasonable amount of time to finish its async operation before navigating away
         from the current page, without, in the case of a slow operation, causing the patient too much friction.
        */
        timeoutWith(300, this.noop()),
        catchError(_error => this.noop()),
      )
      .subscribe(() => {
        if (this.routerLink) {
          this.navigateInternally(newTab);
        } else {
          this.navigateExternally(newTab);
        }
      });
  }

  private targetIsBlankTab(): boolean {
    const targetValue = this.target || this.anchorElement.getAttribute('target');
    return targetValue === '_blank';
  }

  private preventDefaultNavigation() {
    if (this.link) {
      this.link.routerLink = [];
    }
  }

  private navigateInternally(newTab: boolean): void {
    if (newTab) {
      return;
    }

    const pathFragments = Array.isArray(this.routerLink) ? this.routerLink : [this.routerLink];
    // Get the queryParams from the RouterLink if available
    const queryParams = this.link?.queryParams;
    this.router.navigate(pathFragments, {
      state: this.state,
      queryParams: queryParams,
      queryParamsHandling: queryParams ? 'merge' : 'preserve',
    });
  }

  private navigateExternally(newTab: boolean): void {
    const href = this.anchorElement.getAttribute('href');
    this.windowService.redirect(href, { newTab });
  }

  private noop(): Observable<null> {
    return observableOf(null);
  }
}

@NgModule({
  declarations: [TrackLinkDirective],
  exports: [TrackLinkDirective],
})
export class TrackLinkModule {}
